import React from 'react';
import { CheckCircle, Copy, FileSliders, Globe, Loader } from 'lucide-react';
import { ProcessingConfig, CrawlStatusResponse } from '../../types/create';
import { DataSourceType } from 'pages/Knowledge/types';

interface SuccessStepProps {
  datasetResponse?: any | null;
  processingConfig: ProcessingConfig | null;
  isCreatingDataset: boolean;
  onNavigateToDocument: () => void;
  selectedDataSource?: string;
  crawlResults?: CrawlStatusResponse | null;
  selectedPages?: Set<number>;
}

const SuccessStep: React.FC<SuccessStepProps> = ({
  datasetResponse,
  processingConfig,
  isCreatingDataset,
  onNavigateToDocument,
  selectedDataSource = DataSourceType.UploadFiles,
  crawlResults,
  selectedPages
}) => {

  const documents = (!!selectedPages ? crawlResults?.data?.filter((_: any, index: any) => selectedPages?.has(index)) || [] : crawlResults?.data || []);

  if (isCreatingDataset) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-sm text-gray-600">Please wait while creating knowledge base...</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Success Header */}
      <div className="text-start mb-4">
        <div className="flex justify-start items-start">
          <div className="w-6 h-6 bg-green-100 rounded-full flex justify-center">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
          <div className="text-lg font-bold text-gray-900 mb-2 ms-2">Knowledge created</div>
        </div>
        <p className="text-sm text-gray-600">We automatically named the Knowledge, you can modify it at any time.</p>
      </div>

      {/* Knowledge Name Section */}
      <div className="bg-white mb-6">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <FileSliders className="w-6 h-6 text-orange-600" />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-900 mb-2">Knowledge name</label>
            <input
              disabled
              type="text"
              defaultValue={crawlResults?.data && crawlResults.data[0]?.url ? crawlResults.data[0].url : ''}
              className="w-full px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button className="p-2 text-gray-400 hover:text-gray-600">
            <Copy className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Embedding Status */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Embedding completed</h3>

        {/* File/Website Status */}
        {selectedDataSource === DataSourceType.WebScraping ? (
          // Website crawl results
          <div className="space-y-3 mb-6">
            <div className="text-xs text-gray-600 mb-2">
              {documents?.length || 0} pages from website crawl
            </div>
            {crawlResults?.data && selectedPages &&
              documents.slice(0, 3).map((page, displayIndex) => {
                if (!page || page.title === "") return null;

                return (
                  <div key={displayIndex} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Globe className="w-5 h-5 text-blue-500" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium w-[150px] sm:w-[400px] text-gray-900 truncate">{page.title}</div>
                        <div className="text-xs w-[50px] sm:w-[200px] text-gray-500 truncate">{page.url}</div>
                      </div>
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Standard</span>
                    </div>
                    <Loader className="w-5 h-5 text-gray-500 animate-spin" />
                  </div>
                );
              })
            }
            {documents && documents.length > 3 && (
              <div className="text-xs text-gray-500 text-center py-2">
                ... and {documents.length - 3} more pages
              </div>
            )}
          </div>
        ) : (
          // File upload results
          <div className="space-y-3 mb-6">
            {documents && documents.slice(0, 3).map((file: any, index: number) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FileSliders className="w-5 h-5 text-blue-500" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium w-[150px] sm:w-[400px] text-gray-900 truncate">{file.title}</div>
                  </div>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Standard</span>
                </div>
                <Loader className="w-5 h-5 text-gray-500 animate-spin" />
              </div>
            ))}
            {documents && documents.length > 3 && (
              <div className="text-xs text-gray-500 text-center py-2">
                ... and {documents.length - 3} more pages
              </div>
            )}
          </div>
        )}

        {/* Configuration Details */}
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Chunking Setting</span>
            <span className="text-gray-900">Custom</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Maximum Chunk Length</span>
            <span className="text-gray-900">{processingConfig?.rules.segmentation.maxTokens}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Text Preprocessing Rules</span>
            <span className="text-gray-900">
              {processingConfig?.rules.preProcessingRules.find(r => r.enabled)?.id === 'remove_extra_spaces' ?
                'Replace consecutive spaces, newlines and tabs' : 'None'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Index Method</span>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
              <span className="text-gray-900">Economical</span>
            </div>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Retrieval Setting</span>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">⚡</span>
              </div>
              <span className="text-gray-900">Inverted Index</span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="flex justify-start">
        <button
          onClick={onNavigateToDocument}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
        >
          Go to Document
        </button>
      </div>
    </div>
  );
};

export default SuccessStep;
